// Vector glyphs for board sprites (§0.5: numerals are vector glyphs, never fillText): stroked numerals 0–9
// and a few symbols, drawn as open strokes in a box of height h (width 0.62·h).
import type { Ctx2D } from './ctx.ts';
import type { PathFn } from './shapes.ts';

type Cmd = (c: Ctx2D, w: number, h: number) => void;

const E = (cx: number, cy: number, rx: number, ry: number): Cmd => {
  return (c, w, h) => {
    c.moveTo((cx + rx) * w, cy * h);
    c.ellipse(cx * w, cy * h, rx * w, ry * h, 0, 0, Math.PI * 2);
  };
};

const DIGITS: Record<string, Cmd[]> = {
  '0': [E(0.5, 0.5, 0.42, 0.5)],
  '1': [
    (c, w, h) => {
      c.moveTo(0.2 * w, 0.22 * h);
      c.lineTo(0.58 * w, 0);
      c.lineTo(0.58 * w, h);
    },
  ],
  '2': [
    (c, w, h) => {
      c.moveTo(0.08 * w, 0.3 * h);
      c.bezierCurveTo(0.08 * w, -0.08 * h, 0.95 * w, -0.08 * h, 0.92 * w, 0.3 * h);
      c.bezierCurveTo(0.9 * w, 0.55 * h, 0.3 * w, 0.7 * h, 0.06 * w, h);
      c.lineTo(0.95 * w, h);
    },
  ],
  '3': [
    (c, w, h) => {
      c.moveTo(0.1 * w, 0.14 * h);
      c.bezierCurveTo(0.45 * w, -0.1 * h, 1.0 * w, 0.06 * h, 0.86 * w, 0.28 * h);
      c.bezierCurveTo(0.78 * w, 0.44 * h, 0.55 * w, 0.47 * h, 0.4 * w, 0.47 * h);
      c.bezierCurveTo(1.02 * w, 0.47 * h, 1.02 * w, 0.84 * h, 0.8 * w, 0.93 * h);
      c.bezierCurveTo(0.58 * w, 1.04 * h, 0.22 * w, 1.0 * h, 0.06 * w, 0.86 * h);
    },
  ],
  '4': [
    (c, w, h) => {
      c.moveTo(0.7 * w, h);
      c.lineTo(0.7 * w, 0);
      c.lineTo(0.04 * w, 0.68 * h);
      c.lineTo(0.96 * w, 0.68 * h);
    },
  ],
  '5': [
    (c, w, h) => {
      c.moveTo(0.88 * w, 0);
      c.lineTo(0.2 * w, 0);
      c.lineTo(0.14 * w, 0.44 * h);
      c.bezierCurveTo(0.5 * w, 0.3 * h, 0.98 * w, 0.42 * h, 0.94 * w, 0.7 * h);
      c.bezierCurveTo(0.9 * w, 1.02 * h, 0.32 * w, 1.06 * h, 0.06 * w, 0.86 * h);
    },
  ],
  '6': [
    (c, w, h) => {
      c.moveTo(0.82 * w, 0.04 * h);
      c.bezierCurveTo(0.34 * w, 0.02 * h, 0.08 * w, 0.36 * h, 0.08 * w, 0.66 * h);
    },
    E(0.5, 0.7, 0.42, 0.3),
  ],
  '7': [
    (c, w, h) => {
      c.moveTo(0.06 * w, 0);
      c.lineTo(0.94 * w, 0);
      c.lineTo(0.36 * w, h);
    },
  ],
  '8': [E(0.5, 0.25, 0.34, 0.25), E(0.5, 0.73, 0.42, 0.27)],
  '9': [
    E(0.5, 0.3, 0.42, 0.3),
    (c, w, h) => {
      c.moveTo(0.92 * w, 0.32 * h);
      c.bezierCurveTo(0.94 * w, 0.72 * h, 0.66 * w, 0.98 * h, 0.18 * w, 0.96 * h);
    },
  ],
  '!': [
    (c, w, h) => {
      c.moveTo(0.5 * w, 0);
      c.lineTo(0.5 * w, 0.62 * h);
      c.moveTo(0.5 * w, 0.95 * h);
      c.lineTo(0.5 * w, 0.96 * h);
    },
  ],
  '?': [
    (c, w, h) => {
      c.moveTo(0.1 * w, 0.26 * h);
      c.bezierCurveTo(0.1 * w, -0.08 * h, 0.95 * w, -0.08 * h, 0.9 * w, 0.26 * h);
      c.bezierCurveTo(0.86 * w, 0.46 * h, 0.5 * w, 0.44 * h, 0.5 * w, 0.66 * h);
      c.moveTo(0.5 * w, 0.95 * h);
      c.lineTo(0.5 * w, 0.96 * h);
    },
  ],
};

/** The stroke path of a glyph string, left-aligned at (x, y) with height h. */
export function glyphPath(text: string, x: number, y: number, h: number): PathFn {
  const w = h * 0.62;
  const gap = h * 0.22;
  return (c) => {
    let cx = x;
    for (const ch of text) {
      const cmds = DIGITS[ch];
      if (!cmds) throw new Error(`art: no glyph for "${ch}"`);
      c.save();
      c.translate(cx, y);
      for (const cmd of cmds) cmd(c, w, h);
      c.restore();
      cx += w + gap;
    }
  };
}

export function glyphWidth(text: string, h: number): number {
  return text.length * h * 0.62 + (text.length - 1) * h * 0.22;
}

/** A glyph string centred on (cx, cy): cream strokes over a thicker ink stroke, readable on any fill. */
export function drawGlyphs(
  c: Ctx2D,
  text: string,
  cx: number,
  cy: number,
  h: number,
  fg: string,
  ink: string,
  sw: number,
): void {
  const path = glyphPath(text, cx - glyphWidth(text, h) / 2, cy - h / 2, h);
  c.lineCap = 'round';
  c.lineJoin = 'round';
  c.beginPath();
  path(c);
  c.strokeStyle = ink;
  c.lineWidth = sw * 2.2;
  c.stroke();
  c.beginPath();
  path(c);
  c.strokeStyle = fg;
  c.lineWidth = sw;
  c.stroke();
}
